import React, { useState } from 'react'
import { useAppSelector } from 'reduxTyped'
import { BtnV2, l, TextInputV2 } from '@manychat/manyui'

import { IBuyButton } from 'common/builder/buttons/buttonInterfaces'
import cm from 'common/builder/components/builder/ButtonForm/ButtonForm.module.css'
import ButtonTitleInput from 'common/builder/components/builder/ButtonForm/ButtonTitleInput'
import BuyButtonAdditionalUserData from 'common/builder/components/builder/ButtonForm/BuyButtonAdditionalUserData'
import ButtonNextStepPane from 'common/builder/controllers/builder/ButtonNextStepPane'
import ButtonNextStepSelect from 'common/builder/controllers/builder/ButtonNextStepSelect'
import BuyButtonConditionFiltersController from 'common/builder/controllers/builder/BuyButtonConditionFilters'
import ButtonModel from 'common/builder/models/Button'
import * as nextStepSelectors from 'common/builder/selectors/builder/nextStepSelectors'
import * as sourceSelectors from 'common/builder/selectors/builder/sourceSelectors'
import { getCurrentAccount } from 'common/core/selectors/appSelectors'
import FormulaInput from 'common/fields/components/FormulaPriceInput'
import { SettingsTabType } from 'common/settings/constants/SettingsTabType'
import CompactSettings from 'common/settings/controllers/CompactSettings'
import { getSettingsItem } from 'common/settings/selectors/settingsSelectors'
import If from 'components/If/If'
import LabelTooltip from 'components/LabelTooltip'
import { IMinimalChangeEvent } from 'constants/constants'

interface IBuyButtonFormProps {
  builderId: string
  button: IBuyButton
  errors: Array<{ prop: string }>
  showErrors: boolean
  onChange: (changes: Partial<IBuyButton>) => void
}

const BuyButtonForm = (props: IBuyButtonFormProps) => {
  const { builderId, button, errors, showErrors, onChange } = props

  const [priceDirty, setPriceDirty] = useState(false)
  const [labelDirty, setLabelDirty] = useState(false)
  const handlePriceBlur = () => setPriceDirty(true)
  const handleItemLabelBlur = () => setLabelDirty(true)
  const priceError = errors.find((err) => err.prop === 'item_price') && (showErrors || priceDirty)
  const itemLabelError =
    errors.find((err) => err.prop === 'item_label') && (showErrors || labelDirty)

  const paypal = useAppSelector(getSettingsItem)?.payments?.paypal
  const currency = useAppSelector((state) => state.app.paymentCurrency)
  const { is_buy_button_enabled, payments_settings_enabled, payments_enabled } =
    useAppSelector(getCurrentAccount)
  const disableBuyButton = payments_enabled && !payments_settings_enabled && !is_buy_button_enabled

  const target = useAppSelector((state) => sourceSelectors.getTarget(state, builderId, button.id))
  const nextStepType = useAppSelector((state) =>
    sourceSelectors.getNextStepType(state, builderId, button.id),
  )
  const nextStepOptions = useAppSelector((state) =>
    nextStepSelectors.getNextStepOptions(state, builderId, button.id),
  )

  const setAttr = (event: IMinimalChangeEvent) => {
    const { value, name } = event.target
    const isString = typeof value === 'string'
    if (name) {
      onChange({ [name]: isString ? value.replace(/(\n|\r|×)/gm, '') : value })
    }
  }

  if (!is_buy_button_enabled) {
    let disabledText = l.translate('You must have an active payment system connected to Manychat. ')
    if (disableBuyButton) {
      disabledText = l.translate(
        'To edit Buy Button connect payment system account. Unfortunately, you have insufficient rights to do that. Please, contact page’s Owner',
      )
    }
    return (
      <div className="p-x p-b">
        <div className="m-y">{disabledText}</div>
        <If condition={payments_settings_enabled}>
          <CompactSettings defaultTab={SettingsTabType.PAYMENT}>
            <BtnV2 variant="primary" fullWidth>
              {l.translate('Go To Settings')}
            </BtnV2>
          </CompactSettings>
        </If>
      </div>
    )
  }

  return (
    <div className="p-x p-b" style={{ maxHeight: 450, width: 310 }}>
      <ButtonTitleInput
        builderId={builderId}
        buttonId={button.id}
        value={ButtonModel.getCaption(button)}
        onChange={setAttr}
        errors={errors}
        showErrors={showErrors}
      />

      <label className="form-label">
        {l.translate('Item Price ({currency}):', { currency })}{' '}
        <LabelTooltip>
          {l.translate('The minimum item price must be 0.5 USD or equivalent in another currency')}
        </LabelTooltip>
      </label>
      <FormulaInput
        name="item_price"
        value={button.item_price}
        onBlur={handlePriceBlur}
        onChange={setAttr}
        isValid={!priceError}
      />

      <TextInputV2
        name="item_label"
        value={button.item_label}
        onBlur={handleItemLabelBlur}
        onChange={setAttr}
        invalid={itemLabelError}
        label={l.translate('Item Name')}
        infoText={l.translate('Item Name is used in order history. It isn’t visible to clients')}
        className="m-t"
      />

      <BuyButtonAdditionalUserData button={button} paypal={paypal} onChange={onChange} />

      <div className="p-relative m-t-md">
        <LabelTooltip className={cm.buyConditionToolTip}>
          {l.translate(
            'This optional setting allows you to limit the purchase depending on selected conditions.',
          )}
        </LabelTooltip>
        <BuyButtonConditionFiltersController {...{ buttonId: button.id, builderId }} />
      </div>

      <div className="m-b">
        <label className="form-label m-b">{l.translate('When the Purchase is Successful')}</label>
        <If condition={!target}>
          <ButtonNextStepSelect
            options={nextStepOptions}
            builderId={builderId}
            // @ts-expect-error react-redux connect don't adds controller propTypes
            buttonId={button.id}
            keepType
          />
        </If>
        <If condition={Boolean(target)}>
          <ButtonNextStepPane
            type={nextStepType}
            builderId={builderId}
            buttonId={button.id}
            button={button}
            target={target}
            keepType
          />
        </If>
      </div>
    </div>
  )
}

export default BuyButtonForm
